import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getWebApp, haptic } from '@/lib/telegram';
import { detectOS, getDeeplinkForOS, getOsLabel, getClientNameForOS } from '@/lib/os-detect';
import { ShieldCheck, Copy, Check, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { selectSubscription } from '@/lib/use-selected-subscription';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import QRCode from 'qrcode';

export const Route = createFileRoute('/success/$subscriptionId')({
  component: Success,
});

function Success() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subscriptionId } = Route.useParams();
  const os = detectOS();
  const [showOther, setShowOther] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const queryClient = useQueryClient();

  // Фильтр по subscriptionId из URL — у клиента может быть несколько подписок,
  // нужна именно та, которую только что купили/обновили.
  const { data: subs } = useQuery({
    queryKey: ['subs'],
    queryFn: () => api.allSubscriptions(),
    // Только что купленная подписка — кэш мог быть протухшим (нет нового sub или
    // пустой configUrl → пустой QR). Всегда тянем свежее при заходе на экран.
    refetchOnMount: 'always',
  });
  const sub = subs?.find((s) => s.id === subscriptionId) ?? null;
  const configUrl = sub?.configUrl ?? '';

  // Главная открывается на только что купленной подписке, а не на прошлой.
  useEffect(() => {
    selectSubscription(subscriptionId);
  }, [subscriptionId]);

  useEffect(() => {
    haptic('success');
    const tg = getWebApp();
    if (!tg?.MainButton) return;
    tg.MainButton.setText(t('Go to dashboard').toUpperCase());
    tg.MainButton.show();
    const onClick = () => navigate({ to: '/me' });
    tg.MainButton.onClick(onClick);
    return () => {
      tg.MainButton.offClick(onClick);
      tg.MainButton.hide();
    };
  }, [navigate, t]);

  useEffect(() => {
    if (!showOther || qrSvg || !configUrl) return;
    QRCode.toString(configUrl, {
      type: 'svg',
      errorCorrectionLevel: 'M',
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((svg) => setQrSvg(svg))
      .catch(() => setQrSvg(null));
  }, [showOther, qrSvg, configUrl]);

  const handleConnectHero = () => {
    if (!configUrl) return;
    const link = getDeeplinkForOS(os, configUrl);
    // Telegram WebApp:
    //   - `tg.openLink` — только https-URL, на custom-scheme (happ://) silently failing
    //   - `window.location.href = happ://...` — на iOS sandbox блокирует
    //   - программный клик по <a href="happ://...">  — единственный путь, iOS обрабатывает
    //     это как нативный tap, и схема резолвится в установленное приложение.
    try {
      const a = document.createElement('a');
      a.href = link;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      // Последний fallback: пытаемся через tg.openLink (если вдруг scheme поддержан)
      try {
        getWebApp()?.openLink(link);
      } catch {
        window.location.href = link;
      }
    }
    // Сразу раскрываем «другое устройство» — если app не установлен / scheme не сработал,
    // юзер видит QR + copy fallback.
    setShowOther(true);
    haptic('light');
    const clientName = getClientNameForOS(os);
    toast.info(
      t('Opening client', { client: clientName }),
      { duration: 5000 },
    );
  };

  const handleCopy = async () => {
    if (!configUrl) return;
    try {
      await navigator.clipboard.writeText(configUrl);
      setCopied(true);
      toast.success(t('Copied'));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('Clipboard unavailable'));
    }
  };

  // Отзыв ссылки. Двухшаговое подтверждение: действие необратимое, и после него
  // придётся заново импортировать профиль на всех своих устройствах.
  const handleRotate = async () => {
    if (!sub || rotating) return;
    if (!confirmRotate) {
      setConfirmRotate(true);
      haptic('light');
      setTimeout(() => setConfirmRotate(false), 5000);
      return;
    }
    setConfirmRotate(false);
    setRotating(true);
    try {
      await api.rotateSubscriptionLink(sub.id);
      // QR построен по старому адресу — сбрасываем, иначе покажет мёртвую ссылку.
      setQrSvg(null);
      await queryClient.invalidateQueries({ queryKey: ['subs'] });
      haptic('success');
      toast.success(t('Link rotated'));
    } catch (e) {
      const tooSoon =
        e instanceof Error && e.message.includes('link_rotation_too_soon');
      toast.error(
        tooSoon
          ? t('Rotate once per hour')
          : t('Rotate failed generic'),
      );
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="p-6 flex flex-col items-center text-center space-y-6 pt-12 pb-24">
      <div className="relative w-24 h-24 flex items-center justify-center mb-2">
        <div className="absolute inset-0 rounded-full bg-[#38e0a5] blur-[40px] opacity-30 pointer-events-none" />
        <div className="relative w-24 h-24 rounded-full flex items-center justify-center" style={{ background: 'rgba(56,224,165,0.14)' }}>
          <ShieldCheck className="w-12 h-12 text-[#5fe3ad]" />
        </div>
      </div>
      <h1 className="text-[28px] font-extrabold tracking-tight text-foreground">{t('Subscription Ready')}</h1>
      <p className="text-[#c2c4ca]">{t('Subscription Ready desc')}</p>

      <button
        type="button"
        onClick={() => {
          if (!configUrl) return;
          // Открываем нашу subscription-страницу в Safari. На ней есть полноценный
          // UI: copy ссылки, deeplink-кнопки для всех VPN-клиентов (INCY / Happ /
          // V2RayTun / Streisand), QR-код. Auto-deeplink убрали — Happ deeplink
          // через iOS Mini App песочницу часто не срабатывает; пусть юзер выберет
          // приложение сам.
          // Адрес берём с бэка (configUrl), а не собираем из id: публичная ссылка
          // живёт на ротируемом токене, и её можно отозвать кнопкой ниже.
          const subUrl = configUrl;
          const tg = getWebApp();
          try {
            if (tg?.openLink) tg.openLink(subUrl);
            else window.open(subUrl, '_blank');
          } catch {
            window.location.href = subUrl;
          }
          haptic('light');
        }}
        className="w-full calm-cta h-14 text-lg font-bold rounded-2xl active:scale-[0.98] transition-transform"
      >
        {t('Open connect page')}
      </button>

      <div className="w-full pt-2">
        <button type="button" className="text-accent font-medium" onClick={() => setShowOther(!showOther)}>
          {t('Connect another device')}
        </button>
        {showOther && (
          <Card className="mt-4 text-left">
            <CardContent className="p-4 space-y-4">
              <p className="text-sm text-[#8a8c93]">{t('Connect other devices desc')}</p>

              <div className="bg-white rounded-2xl p-4 flex items-center justify-center">
                {qrSvg ? (
                  <div className="w-full max-w-[260px] aspect-square" dangerouslySetInnerHTML={{ __html: qrSvg }} />
                ) : (
                  <Skeleton tone="light" className="w-full max-w-[260px] aspect-square rounded-xl" />
                )}
              </div>

              <button
                type="button"
                onClick={handleCopy}
                className="calm-card p-3 rounded-lg flex items-center justify-between overflow-hidden w-full text-left active:scale-[0.98] transition-transform"
              >
                <code className="text-xs truncate max-w-[80%] text-[#c2c4ca]">{configUrl}</code>
                {copied ? <Check className="w-4 h-4 text-[#5fe3ad] shrink-0" /> : <Copy className="w-4 h-4 text-accent shrink-0" />}
              </button>

              {/* Отзыв ссылки: для случая «поделился и передумал». Честно пишем,
                  что уже подключённые устройства это не отключит: ключи живут на
                  серверах провайдера и нашему адресу не подчиняются. */}
              <div className="pt-1 space-y-2">
                <button
                  type="button"
                  onClick={handleRotate}
                  disabled={rotating || !sub}
                  className="w-full p-3 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-transform active:scale-[0.98] disabled:opacity-50"
                  style={{
                    background: confirmRotate ? 'rgba(255,107,107,0.14)' : 'rgba(255,255,255,0.035)',
                    color: confirmRotate ? '#ff8a8a' : '#c2c4ca',
                  }}
                >
                  <RotateCcw className={`w-4 h-4 shrink-0 ${rotating ? 'animate-spin' : ''}`} />
                  {rotating
                    ? t('Rotating')
                    : confirmRotate
                      ? t('Rotate confirm')
                      : t('Rotate link')}
                </button>
                <p className="text-[11px] leading-snug text-[#8a8c93]">
                  {t('Rotate note long')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
